import { hasBranches } from 'dashboard/lib/workflows';
import {
  WorkflowStep,
  WorkflowStepActionKey,
  WorkflowStepConditional,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';

/**
 * Return steps represented by selected ActionNode,
 * excluding the update_next_step action (and conditional step(s), by definition)
 * @param steps: An original stepsConfig
 * @param targetToken: A token of a step represented by an ActionsNode in ITE flow editor
 */
export const getActionNodeSteps = (steps: WorkflowStep[], targetToken: string): WorkflowStep[] => {
  const matchingStepIndex = steps.findIndex((step) => step.token === targetToken);
  const matchingStep = steps[matchingStepIndex];
  if (matchingStep != null) {
    if (matchingStep.type === WorkflowStepType.Conditional) {
      // Filter branches of matching step to only contain update next step action
      return [
        {
          ...matchingStep,
          branches: matchingStep.branches.map((branchSteps) =>
            branchSteps.filter(
              (step) =>
                step.type === WorkflowStepType.Action &&
                step?.action?.key === WorkflowStepActionKey.UpdateInquiryNextStepName,
            ),
          ),
        },
      ];
    } else {
      // matching step is an action step -- omit conditional branches
      return steps.filter(
        (step) =>
          step.type !== WorkflowStepType.Conditional &&
          !(
            step.type === WorkflowStepType.Action &&
            step?.action?.key === WorkflowStepActionKey.UpdateInquiryNextStepName
          ),
      );
    }
  } else {
    return (
      steps
        .find((step): step is WorkflowStepConditional => step.type === WorkflowStepType.Conditional)
        ?.branches.flatMap((branch) => getActionNodeSteps(branch, targetToken))
        .filter(Boolean) ?? []
    );
  }
};

/**
 *
 * Return new stepsConfig with replacing a step matched token
 * @param steps: An original stepsConfig
 * @param newStep: A step which will be used to replace
 *
 **/
export const findAndReplaceSteps = (
  steps: WorkflowStep[],
  newStep: WorkflowStep,
): WorkflowStep[] => {
  return steps.map((step) => {
    if (step.token === newStep.token) {
      return newStep;
    } else if (hasBranches(step)) {
      return {
        ...step,
        branches: step.branches.map((branchSteps) => findAndReplaceSteps(branchSteps, newStep)),
      };
    } else {
      return step;
    }
  });
};

/**
 *
 * Return new stepsConfig with omitting a step matched token. A blank branch of Parallel step will be omitted as well
 * @param steps: An original stepsConfig
 * @param targetToken: A token of a step which will be omitted
 *
 **/
export const omitStep = (steps: WorkflowStep[], targetToken: string): WorkflowStep[] =>
  steps.reduce<WorkflowStep[]>((newSteps, step) => {
    if (step.token === targetToken) {
      return newSteps;
    } else if (step.type === WorkflowStepType.Parallel) {
      return [
        ...newSteps,
        {
          ...step,
          branches: step.branches
            .map((branch) => {
              if (branch.length === 1 && branch[0].token === targetToken) {
                return [];
              } else {
                return omitStep(branch, targetToken);
              }
            })
            .filter((branch) => branch.length > 0),
        },
      ];
    } else if (step.type === WorkflowStepType.Conditional) {
      return [
        ...newSteps,
        {
          ...step,
          branches: step.branches.map((branch) => omitStep(branch, targetToken)),
        },
      ];
    } else {
      return [...newSteps, step];
    }
  }, []);

/**
 * Return new stepConfig inserting a new step that follows a given step token
 * @param steps: An original stepsConfig
 * @param previousStepToken: The step to insert the new step after
 * @param newStep: A new step to add to the stepsConfig
 */
export const findAndAddNextStep = (
  steps: WorkflowStep[],
  previousStepToken: string,
  newStep: WorkflowStep,
): WorkflowStep[] => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return steps.reduce((previousSteps, step) => {
    if (step.token === previousStepToken) {
      return [...previousSteps, step, newStep];
    } else if (hasBranches(step)) {
      return [
        ...previousSteps,
        {
          ...step,
          branches: step.branches.map((branchSteps) =>
            findAndAddNextStep(branchSteps, previousStepToken, newStep),
          ),
        },
      ];
    } else {
      return [...previousSteps, step];
    }
  }, []);
};

/**
 * Returns a new stepConfig with a new step inserted at the beginning of a branch
 * @param steps: An original stepsConfig
 * @param newStep: A new step to add to the stepsConfig
 * @param parentStepToken: The parent of the branch to be inserted into
 * @param branchIndex: The index of the branch to be inserted into
 */
export const findAndAddFirstBranchStep = (
  steps: WorkflowStep[],
  newStep: WorkflowStep,
  parentStepToken: string,
  branchIndex: number,
): WorkflowStep[] => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return steps.reduce((previousSteps, step) => {
    if (step.token === parentStepToken && hasBranches(step)) {
      const branches = step.branches;
      const newBranch = [newStep, ...branches[branchIndex]];

      const newBranches = [
        ...branches.slice(0, branchIndex),
        newBranch,
        ...branches.slice(branchIndex + 1),
      ];

      return [
        ...previousSteps,
        {
          ...step,
          branches: newBranches,
        },
      ];
    } else if (hasBranches(step)) {
      return [
        ...previousSteps,
        {
          ...step,
          branches: step.branches.map((branchSteps) =>
            findAndAddFirstBranchStep(branchSteps, newStep, parentStepToken, branchIndex),
          ),
        },
      ];
    } else {
      return [...previousSteps, step];
    }
  }, []);
};
